import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DashboardLoader, downloadFile, formatRelativeTimeForTable, NoDataComponent, renderHeaderActionButtons, SidePopover, TableDashboard, useAppDispatch, useAppSelector } from '../../../globalUtils/globalExports';
import { cloneDeep } from 'lodash';
import { fetchAcChargingHistoryReportData, getAcFilterReducer, getAcReferenceDataReducer, setAcReportListTableProps } from '../../rmsReduxStore/acReduxExports';
import getAcReportReducer from '../../rmsReduxStore/acreportsRedux/acReportSelector';
import { getAcFilterSelectedValue, getCurrentAcFiltersValues, getCurrentScreenAppliedAcFiltersCount, setAcFilterData } from '../../rmsReduxStore/acFiltersRedux/acFiltersCreators';
import { Id, toast } from 'react-toastify';
import access from '../../../auth/service/AccessControl';
import { exportAcChargingHistoryReport } from '../../rmsReduxStore/acreportsRedux/acReportCreator';
import { Menu, MenuItem } from '@mui/material';
import AcFilterLayout, { FilterProp } from '../../../globalUtils/FilterLayout/AcFilterLayout';

const AcChargerHistoryDashboard = () => {
 
  const dispatch = useAppDispatch ();
  const {chargingHistoryData ,chargingHistoryColumnHeaders,chargingHistoryCount,reportLoader, chargingHistoryListTableProps} = useAppSelector(getAcReportReducer);
  const {chargerVisibleIdReferenceData, chargingStationReferenceData, chargerProductTypeReferenceData} = useAppSelector(getAcReferenceDataReducer);
  const {screenFilters} = useAppSelector(getAcFilterReducer)
  const [exportAnchorEl, setExportAnchorEl] = useState(null);
  const [filtersVisible,setFiltersVisible] = useState<boolean>(false);
  const [dashboardLoader, setDashboardLoader] = useState(false)
  const [exportingFile, setExportingFile] = useState(false);
  const isOrgTypeCustomer = access?.isOrgTypeCustomer();
 const chargingHistoryListTablePropsRefs = useRef(chargingHistoryListTableProps)

  useEffect(() => {
    chargingHistoryListTablePropsRefs.current = chargingHistoryListTableProps;
  }, [chargingHistoryListTableProps])

  const chargerHistoryColumns = chargingHistoryColumnHeaders?.map(item => {
    return ({ field: item?.key, description: item?.description, flex: 1, headerName: item?.visibleName,sortable: item?.key==='beginTime'  ,minWidth: (item?.visibleName?.length > 25) ? 200 : 150,pinnable:false})
  })

 const getTableRows = useCallback(() => {
    if (!chargingHistoryData || !chargingHistoryColumnHeaders) {
      return [];
    }
    const data = cloneDeep(chargingHistoryData)
    chargingHistoryColumnHeaders?.forEach((column) => {
      const { key, dataType, unit } = column;
      if (key && (dataType || unit)) {

        data?.forEach((historyItem) => {
          if (historyItem?.[key] && dataType === 'Date' && historyItem[key] !== 'NA') {
            historyItem[key] = formatRelativeTimeForTable(historyItem[key]);
          }
          if (unit && historyItem[key] !== 'NA') {
            historyItem[key] = `${historyItem[key]} ${unit}`;
          }
        })
      }
    })
    return data || [];
  }, [chargingHistoryData, chargingHistoryColumnHeaders]);


  const mobileViewConfiguration = useMemo(() => {
    return {
      headerDataConfig: {
        headerLeftDataConfig: ['name', 'type'],
        headerRightDataConfig: {
          actionIconsComponent: false,
          statusIconComponent: true,
        },
      },
      statusIconKey: 'status',
      contentDataConfig: [
        [],
      ],
    };
  }, []);

useEffect (()=>{
    dispatch(fetchAcChargingHistoryReportData({
      sortBy: '',
      order: 'ascending',
      pageSize: 15,
      pageNumber : 1,
      filters: getCurrentAcFiltersValues('acchargingHistoryReport'),
      searchTerm:''
    }));
},[dispatch])


  const headerActionBtns = renderHeaderActionButtons({
    actionBtns:
      [
        {
          id: 'add_filter_btn',
          isAccessible: true,
          headerActionBtnClick: (): void => {
            setFiltersVisible(true);
          },
          badgeCount: getCurrentScreenAppliedAcFiltersCount('acchargingHistoryReport')
        },
        {
          id: 'export_csv_btn',
          isAccessible:isOrgTypeCustomer ? true: access?.reports?.chargingHistory?.export(),
          headerActionBtnClick: (event): void => {
            event?.stopPropagation()
            setExportAnchorEl(event.currentTarget);
          }
        }
      ]
  })

  const layoutCallback = useCallback(async (pageNumber: number, pageSize: number, view: string, sortField: string, sortOrder: string, filterValue, searchTerm) => {
    const tableProps: IListTableProps = cloneDeep(chargingHistoryListTablePropsRefs.current)
    await dispatch(fetchAcChargingHistoryReportData({
      sortBy: sortField || '',
      order: sortOrder || 'ascending',
      pageSize,
      pageNumber,
      filters: filterValue ? filterValue : getCurrentAcFiltersValues('acchargingHistoryReport'),
      searchTerm: searchTerm != null && searchTerm != undefined ? searchTerm : chargingHistoryListTablePropsRefs?.current?.searchTerm
    }));
    if (tableProps) {
      tableProps.view = view || 'list'
      tableProps.sortBy = sortField || ''
      tableProps.order = sortOrder || 'ascending';
      tableProps.pageNumber = pageNumber;
      tableProps.pageSize = pageSize;
      if (searchTerm != null && searchTerm != undefined) {
        tableProps.searchTerm = searchTerm;
      }
    }
    await dispatch(setAcReportListTableProps(tableProps, 'chargingHistory'))
  }, [chargingHistoryListTablePropsRefs, dispatch]);


  const handleFiltersClosePopOver = (): void => {
    setFiltersVisible(false);
  }

    const onResetButtonClick = useCallback(async (callList: boolean) => {
        const res = await setAcFilterData(null, '', 'acchargingHistoryReport', 'CLEAR',false)(dispatch);
        if (res?.message === 'Action dispatched successfully') {
          // Reset the filters
          if (callList && chargingHistoryListTablePropsRefs?.current) {
            const tableProps = cloneDeep(chargingHistoryListTablePropsRefs?.current);
            await layoutCallback(
              1,
              tableProps?.pageSize,
              tableProps?.view,
              tableProps?.sortBy,
              tableProps?.order,
              res?.filters,
              tableProps?.searchTerm ?? '',
            )
          }
        }
    
      }, [chargingHistoryListTablePropsRefs?.current])

  const dateRangeExceededErrorToast = (message): Id => toast.error(message);

    const dateFilterRangeCheck = (daysLimit) => {
      const filterSetOnValue = getAcFilterSelectedValue('chargingHistoryInterval', 'acchargingHistoryReport', false);
      const startDate = filterSetOnValue?.[0] ? new Date(filterSetOnValue?.[0]) : null;
      const endDate = filterSetOnValue?.[1] ? new Date(filterSetOnValue?.[1]) : null;
      if (startDate && endDate) {
        const differenceInMilliseconds = endDate.getTime() - startDate.getTime();
        const differenceInDays = differenceInMilliseconds / (1000 * 60 * 60 * 24);
        if (differenceInDays < daysLimit) {
          return true;
        }
      }
      return false;
    }

  const fileDownloadSuccessToast = useCallback((): Id => {
    setExportingFile(false);
    return toast.success('File downloaded.');
  }, []);

  const fileDownloadFailureToast = useCallback((): Id => {
    setExportingFile(false);
    return toast.warn('Something went wrong. Please export again.');
  }, []);


    const onExportBtnClick = useCallback(async (fileType: 'csv' | 'xlsx'): Promise<void> => {
      if (!dateFilterRangeCheck(5)) {
        const filterSetOnValue = getAcFilterSelectedValue('chargingHistoryInterval', 'acchargingHistoryReport', false);
        const startDate = filterSetOnValue?.[0] ? filterSetOnValue?.[0] : null;
        const endDate = filterSetOnValue?.[1] ? filterSetOnValue?.[1] : null;
        if (startDate && endDate) dateRangeExceededErrorToast('Date range exceeded more than 5 days!!')
        else dateRangeExceededErrorToast('Please select date range of 5 days!!')
        return;
      }
  
      if (exportingFile) {
        // If already exporting, prevent another export
        return;
      }
      try {
        setDashboardLoader(true)
        const response = await dispatch(exportAcChargingHistoryReport({
          fileType: fileType,
          filters: getCurrentAcFiltersValues('acchargingHistoryReport')
        }));
        if (response?.status === 202 || response?.status === 200) {
          setDashboardLoader(false)
          downloadFile({ url: response?.data?.url });
          fileDownloadSuccessToast()
        } else {
          setDashboardLoader(false)
          fileDownloadFailureToast();
          setExportAnchorEl(null);
        }
      } finally {
        setDashboardLoader(false)
        // Ensure that exporting status is updated even if there's an error
        setExportingFile(false);
        setExportAnchorEl(null);
      }
    }, [exportingFile, fileDownloadSuccessToast, fileDownloadFailureToast])
  


      const renderExportMenuItem = (fileType: 'csv' | 'xlsx', label: string): JSX.Element => (
    <MenuItem key={fileType}>
      <div className='export__btn-option' onClick={(e) => {
        e?.stopPropagation();
        onExportBtnClick(fileType);
      }}>
        {label}
      </div>
    </MenuItem>
  );


    const handleFilterDataChange = useCallback(async (val, filterKey, isGlobal) => {
      const res = await setAcFilterData(val, filterKey, 'acchargingHistoryReport', 'SET', isGlobal ? isGlobal : false)(dispatch)
      if (res?.message === 'Action dispatched successfully') {
          const filterSetOnValue = getAcFilterSelectedValue('chargingHistoryInterval', 'acchargingHistoryReport', false);
          const startDate = filterSetOnValue?.[0] ? filterSetOnValue?.[0] : null;
          const endDate = filterSetOnValue?.[1] ? filterSetOnValue?.[1] : null;
          if (startDate && endDate) {
            const differenceInMilliseconds = endDate.getTime() - startDate.getTime();
            const differenceInDays = differenceInMilliseconds / (1000 * 60 * 60 * 24);
            if (differenceInDays > 6) {
              dateRangeExceededErrorToast('Date range exceeded more than 5 days!!')
              return
            }
          } else if (startDate && !endDate) {
            dateRangeExceededErrorToast('Please select date range of 5 days!!')
            return
          }
          if (chargingHistoryListTablePropsRefs?.current) {
            await layoutCallback(1, chargingHistoryListTablePropsRefs?.current?.pageSize, chargingHistoryListTablePropsRefs?.current?.view, chargingHistoryListTablePropsRefs?.current?.sortBy, chargingHistoryListTablePropsRefs?.current?.order, res?.filters, chargingHistoryListTablePropsRefs?.current?.searchTerm)
          }
      }
    }, [chargingHistoryData, dispatch],)

    const chargingHistoryFilterProps: FilterProp[] = useMemo(() => {
      const filterSetOnValue = getAcFilterSelectedValue('chargingHistoryInterval', 'acchargingHistoryReport', false);
  
      return [
        {
          filterLabel: 'Charging Stations',
          filterType: 'dropdown',
          filterId: 'chargingStationId',
          filterDropdownProps: {
            selectValue: getAcFilterSelectedValue('chargingStationId', 'acchargingHistoryReport', false),
            selectOptions: chargingStationReferenceData ?? [],
            handleSelectOptionChange: (val): void => {
              handleFilterDataChange(val, 'chargingStationId', false)
            },
            selectDropDownId: 'charging-history-charging-station-id-filter-dropdown',
            isMultiSelect: true,
            renderToBody: true,
          }
        },
        {
          filterLabel: 'Chargers',
          filterType: 'dropdown',
          filterId: 'chargerId',
          filterDropdownProps: {
            selectValue: getAcFilterSelectedValue('chargerId', 'acchargingHistoryReport', false),
            selectOptions: chargerVisibleIdReferenceData ?? [],
            handleSelectOptionChange: (val): void => {
              handleFilterDataChange(val, 'chargerId', false)
            },
            selectDropDownId: 'charging-history-charger-id-filter-dropdown',
            isMultiSelect: true,
            renderToBody: true,
          }
        },
        {
          filterLabel: 'Product Type',
          filterType: 'dropdown',
          filterId: 'productType',
          filterDropdownProps: {
          selectValue: getAcFilterSelectedValue('productType', 'acchargingHistoryReport', false),
          selectOptions: chargerProductTypeReferenceData ?? [],
          handleSelectOptionChange: (val): void => {
          handleFilterDataChange(val, 'productType', false)
          },
          selectDropDownId: 'charging-history-product-type-filter-dropdown',
          isMultiSelect: true
          }
        },
        {
          filterLabel: 'Interval',
          filterType: 'dateFilter',
          filterId: 'chargingHistoryInterval',
          dateFilterProps: {
            datePickerId: 'charging__history__time__stamp__set__at__date__picker',
            datePickerClassName: 'charging__history__time__stamp__cleared__at__date__picker',
            selectsRange: true,
            startDate: filterSetOnValue?.[0] ? new Date(filterSetOnValue?.[0]) : null,
            endDate: filterSetOnValue?.[1] ? new Date(new Date(filterSetOnValue?.[1]).getTime()) : null,    
            onChange: (val): void => {
              handleFilterDataChange(val, 'chargingHistoryInterval', false)
            },
          }
        },
      ];
    }, [screenFilters]);


      const filterFooterBtns = useMemo(() => {
          return [
            {
              buttonText: 'Reset',
              buttonId: 'Reset',
              btnClassName: 'secondary__btn',
              handleClick: (): Promise<void> => onResetButtonClick(true),
              isDisabled: false,
              buttonVariant: 'outlined',
            },
          ];
        }, [onResetButtonClick]);


      const handleClose = (): void => {
    setExportAnchorEl(null);
  };
  
    return <>
      <DashboardLoader showLoader={dashboardLoader} />
      <TableDashboard
        tableId="manage-charging-history-table"
        tableClassName="manage__charging__history__table"
        header={'Charging History'}
        searchBoxIncluded={true}
        gridColumns={chargerHistoryColumns}
        headerActionBtns={headerActionBtns}

        tableRows={getTableRows()}
        mobileViewConfiguration={mobileViewConfiguration}
        layoutView={'list'}
        listLayoutCallBack={layoutCallback}
        totalCount={chargingHistoryCount[0]?.count}
        showSkeleton={!chargingHistoryData}
        totalCountText={'charging history'}
        showLoader={reportLoader}
        customNoDataComponent={NoDataComponent}
        pageNumber={chargingHistoryListTableProps?.pageNumber}
        tablePageSize={chargingHistoryListTableProps?.pageSize}
        // filterProps={chargingHistoryFilterProps}
        // handleFilterDataChange={handleFilterDataChange}
        referenceDataCallbacksFilters={['chargerVisibleId']}
        leftPinnedColumns={['chargerId','connectorNo']}
        screen = 'charging_history'
        enableCustomPagination={true}
      />
            <Menu
              id="export-option-dropdown"
              anchorEl={exportAnchorEl}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              className='export__option__dropdown'
              sx={{ top: '30px' }}
              open={Boolean(exportAnchorEl)}
              onClose={handleClose}
            >
              {renderExportMenuItem('csv', 'To CSV')}
              {renderExportMenuItem('xlsx', 'To Excel')}
            </Menu>

             <>
        {
          filtersVisible && <SidePopover
            id={'manage-version-releases-filters'}
            open={filtersVisible}
            containerClassName={'version-releases-side-filters'}
            handleClose={handleFiltersClosePopOver}
            headerText={'Add Filters'}
            // eslint-disable-next-line react/no-children-prop
            children={<AcFilterLayout requiredReferenceDataCallbacks={['chargerVisibleId', 'customer','chargingStationId', 'productType']}
              filters={chargingHistoryFilterProps} handleFilterDataChange={handleFilterDataChange} excludeGlobalFilters = {false}
            />}
            btnsList={filterFooterBtns}
          />
        }
      </>
 
    </>;
};

export default AcChargerHistoryDashboard;

